import { useEffect, useId, useRef } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { SendHorizontalIcon, XIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Button } from '@rocco/components/button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from '@rocco/components/form';
import { Textarea } from '@rocco/components/textarea';
import {
    validateChatUploadFile,
    validateChatUploadFileCount,
} from '../../utils/validation';

const formSchema = z.object({
    message: z
        .string()
        .trim()
        .max(2000, { message: 'Maximum 2000 characters' }),
    attachments: z.array(z.instanceof(File)),
});

/**
 * The text input for the chat box.
 * - The chat box text size should remain the same regardless of window size,
 *   unlike the rest of the website, so it should use `px` instead of `rem`.
 * @see {@link ChatBox}
 */
export function ChatBoxInput({
    className,
    isOpen,
    isDisabled = false,
    isSubmitting = false,
    isNotParticipant = false,
    autoFocus = true,
    onSend,
}: {
    className?: string;
    /**
     * Whether the chat box is open.
     */
    isOpen: boolean;
    /**
     * Whether the send button is disabled. Defaults to `false`.
     */
    isDisabled?: boolean;
    /**
     * Whether the form is submitting. Only used when sending a message with
     * file attachments. Defaults to `false`.
     */
    isSubmitting?: boolean;
    /**
     * The current user is not a participant of the conversation. If true, the
     * form will be disabled and the input will show a different placeholder message.
     */
    isNotParticipant?: boolean;
    /**
     * Auto focus the input when the chat box is opened. Defaults to `true`.
     */
    autoFocus?: boolean;
    /**
     * Callback when the user sends a message.
     */
    onSend?: ({
        message,
        attachments,
    }: {
        message: string;
        attachments: File[];
    }) => void;
}) {
    const formId = useId();
    const formRef = useRef<HTMLFormElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            message: '',
            attachments: [],
        },
    });
    const message = form.watch('message');
    const attachments = form.watch('attachments');
    const isSendDisabled =
        isDisabled ||
        isSubmitting ||
        isNotParticipant ||
        (!message.trim() && !attachments.length);

    function onFilesAdded(files: FileList | null) {
        if (!files?.length) {
            return;
        }
        // TODO: tracker
        const newAttachments = [...form.getValues('attachments')];
        const errors = new Set<string>();
        for (const file of files) {
            const isDuplicate = newAttachments.some(
                (f) => f.name === file.name && f.size === file.size
            );
            if (isDuplicate) {
                continue;
            }
            const isFileCountValid = validateChatUploadFileCount(
                newAttachments.length + 1
            );
            if (typeof isFileCountValid === 'object') {
                errors.add(isFileCountValid.message);
                break;
            }
            const validationResult = validateChatUploadFile(file);
            if (validationResult.valid) {
                newAttachments.push(file);
            } else {
                errors.add(validationResult.message);
            }
        }
        form.setValue('attachments', newAttachments);
        for (const error of errors) {
            toast.error(error, {
                cancel: {
                    label: <XIcon className="size-4" />,
                    onClick: () => {},
                },
                cancelButtonStyle: {
                    backgroundColor: 'transparent',
                    minWidth: 0,
                },
            });
        }
        textareaRef.current?.focus();
    }

    function onAttachmentRemove(index: number) {
        form.setValue(
            'attachments',
            attachments.filter((_, i) => i !== index)
        );
    }

    function onSubmit(values: z.infer<typeof formSchema>) {
        if (isSendDisabled) {
            return;
        }
        onSend?.({ message: values.message, attachments: values.attachments });
        form.reset();
        textareaRef.current?.focus();
    }

    useEffect(() => {
        // Focus the input when the chat box is opened.
        if (isOpen && autoFocus) {
            textareaRef.current?.focus();
        }
    }, [isOpen, autoFocus]);

    return (
        <Form {...form}>
            <form
                ref={formRef}
                id={`chat-box-input-${formId}`}
                className={cn('flex flex-col gap-[8px]', className)}
                onSubmit={form.handleSubmit(onSubmit)}
            >
                <FormField
                    name="message"
                    control={form.control}
                    render={({ field }) => (
                        <FormItem className="space-y-[8px]">
                            <FormControl ref={textareaRef}>
                                <Textarea
                                    // Need ! to override md:text-base class
                                    className="min-h-0 resize-none !rounded-[8px] px-[12px] py-[8px] text-[16px] leading-tight md:!text-[14px] md:!leading-tight"
                                    color="contrast"
                                    {...field}
                                    disabled={
                                        field.disabled || isNotParticipant
                                    }
                                    placeholder={
                                        !isNotParticipant
                                            ? 'Write a message...'
                                            : 'Signed in as ArchiPro staff - sending is disabled'
                                    }
                                    rows={3}
                                    autoFocus={autoFocus}
                                    autoComplete="off"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            if (
                                                e.ctrlKey ||
                                                e.altKey ||
                                                e.shiftKey
                                            ) {
                                                // Ctrl, Alt, or Shift + Enter to insert a new line.
                                                e.preventDefault();
                                                form.setValue(
                                                    'message',
                                                    field.value + '\n'
                                                );
                                            } else {
                                                // Just Enter to send the message.
                                                formRef.current?.requestSubmit();
                                            }
                                        }
                                    }}
                                />
                            </FormControl>
                            <FormMessage
                                className="text-[12px]"
                                variant="darker"
                            />
                        </FormItem>
                    )}
                />
                {attachments.length > 0 && (
                    <ul className="flex flex-wrap gap-[8px] text-[12px] leading-tight">
                        {attachments.map((attachment, index) => (
                            <li
                                key={`${attachment.name}-${attachment.size}`}
                                className="flex max-w-full items-center gap-[4px] rounded-md bg-gray-100 p-[8px] hover:bg-gray-200"
                            >
                                <span className="truncate">
                                    {attachment.name}
                                </span>
                                <button
                                    className="shrink-0"
                                    type="button"
                                    aria-label="Remove attachment"
                                    onClick={() => onAttachmentRemove(index)}
                                >
                                    <XIcon className="size-[12px]" />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
                <div className="mt-[20px] flex items-center justify-between gap-[8px]">
                    <Button
                        className="border border-gray-900 hover:bg-white/20"
                        type="button"
                        color="transparent"
                        size="narrow"
                        weight="normal"
                        disabled={isSubmitting || isNotParticipant}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        Upload File
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        multiple
                        onChange={(e) => onFilesAdded(e.target.files)}
                    />
                    <Button
                        className="bg-brick-700 py-2 text-white hover:bg-brick-900 hover:text-white"
                        type="submit"
                        color="primary"
                        size="larger"
                        disabled={isSendDisabled}
                        isLoading={isSubmitting}
                    >
                        Send
                        <SendHorizontalIcon className="ml-2 size-4" />
                    </Button>
                </div>
            </form>
        </Form>
    );
}
